/**
 * Generates and downloads a high-resolution branded Agent Training Voucher
 * as an official certificate pass directly in the browser.
 */
export async function downloadAgentTrainingVoucher({
  couponCode = "ATV-1000",
  userName = "Certified Agent",
  userPhone = "",
  rankLevel = 7,
  directsCount = 5,
  amount = 1000,
  activatedAt = "",
  pdfUrl = "",
}) {
  // If an official admin uploaded PDF exists, open/download it directly
  if (pdfUrl) {
    try {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = `Agent_Training_Voucher_${couponCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    } catch (_) {}
  }

  // Generate high-resolution luxury voucher certificate pass via HTML5 Canvas
  const width = 1200;
  const height = 675;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // 1. Background Gradient (Emerald to Deep Slate / Navy)
  const bgGrad = ctx.createLinearGradient(0, 0, width, height);
  bgGrad.addColorStop(0, "#064e3b"); // emerald-900
  bgGrad.addColorStop(0.5, "#0f172a"); // slate-900
  bgGrad.addColorStop(1, "#022c22"); // deep emerald
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // 2. Decorative Golden / Emerald Guilloche Border
  ctx.strokeStyle = "#10b981";
  ctx.lineWidth = 4;
  ctx.strokeRect(30, 30, width - 60, height - 60);

  ctx.strokeStyle = "rgba(245, 158, 11, 0.4)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(40, 40, width - 80, height - 80);

  // Corner Accents
  const cornerSize = 24;
  ctx.fillStyle = "#f59e0b";
  [[40, 40], [width - 40, 40], [40, height - 40], [width - 40, height - 40]].forEach(([cx, cy]) => {
    ctx.beginPath();
    ctx.arc(cx, cy, 6, 0, Math.PI * 2);
    ctx.fill();
  });

  // 3. Header & Brand Title
  ctx.fillStyle = "#34d399";
  ctx.font = "900 22px 'Inter', sans-serif";
  ctx.letterSpacing = "4px";
  ctx.fillText("TRIKONEKT • CAPTAIN ACADEMY & ASIYAPP", 80, 95);

  ctx.fillStyle = "#ffffff";
  ctx.font = "900 42px 'Inter', sans-serif";
  ctx.fillText("AGENT TRAINING VOUCHER", 80, 150);

  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 18px 'Inter', sans-serif";
  ctx.fillText("Official LMS Masterclass, Digital Modules & Certification Pass", 80, 185);

  // Divider Line
  ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(80, 215);
  ctx.lineTo(width - 80, 215);
  ctx.stroke();

  // 4. Value Box (Right Side) - Accreditation / Non-Monetary Admission Pass
  const valBoxX = width - 420;
  const valBoxY = 250;
  const valBoxW = 340;
  const valBoxH = 160;

  ctx.fillStyle = "rgba(16, 185, 129, 0.12)";
  ctx.strokeStyle = "rgba(16, 185, 129, 0.4)";
  ctx.lineWidth = 2;
  ctx.roundRect(valBoxX, valBoxY, valBoxW, valBoxH, 16);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#a7f3d0";
  ctx.font = "700 15px 'Inter', sans-serif";
  ctx.fillText("ACCREDITATION PASS", valBoxX + 24, valBoxY + 42);

  ctx.fillStyle = "#ffffff";
  ctx.font = "900 32px 'Inter', sans-serif";
  ctx.fillText("ADMISSION PASS", valBoxX + 24, valBoxY + 92);

  ctx.fillStyle = "#6ee7b7";
  ctx.font = "700 13px 'Inter', sans-serif";
  ctx.fillText("OFFICIAL LEADERSHIP INDUCTION • L7+", valBoxX + 24, valBoxY + 130);

  // 5. Agent & Qualification Details (Left Side)
  ctx.fillStyle = "#94a3b8";
  ctx.font = "600 15px 'Inter', sans-serif";
  ctx.fillText("QUALIFIED BENEFICIARY", 80, 270);

  ctx.fillStyle = "#ffffff";
  ctx.font = "900 28px 'Inter', sans-serif";
  ctx.fillText(userName, 80, 305);

  if (userPhone) {
    ctx.fillStyle = "#cbd5e1";
    ctx.font = "600 17px 'Inter', sans-serif";
    ctx.fillText(`Registered ID: ${userPhone}`, 80, 335);
  }

  // Qualification Badge
  ctx.fillStyle = "rgba(245, 158, 11, 0.15)";
  ctx.strokeStyle = "rgba(245, 158, 11, 0.5)";
  ctx.lineWidth = 1.5;
  ctx.roundRect(80, 360, 420, 52, 10);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#f59e0b";
  ctx.font = "900 16px 'Inter', sans-serif";
  ctx.fillText(`★ Layer ${rankLevel} Rank Achieved • ${directsCount} Direct Sponsors Verified`, 96, 392);

  // 6. Voucher Code Box (Bottom Bar)
  const codeBoxY = 460;
  ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
  ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
  ctx.lineWidth = 1.5;
  ctx.roundRect(80, codeBoxY, width - 160, 95, 12);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#94a3b8";
  ctx.font = "700 13px 'Inter', sans-serif";
  ctx.fillText("UNIQUE VOUCHER CODE", 104, codeBoxY + 32);

  ctx.fillStyle = "#38bdf8";
  ctx.font = "900 32px 'Courier New', monospace";
  ctx.fillText(couponCode, 104, codeBoxY + 70);

  // Status Badge in Code Box
  ctx.fillStyle = "rgba(16, 185, 129, 0.25)";
  ctx.strokeStyle = "#10b981";
  ctx.lineWidth = 1.5;
  ctx.roundRect(width - 340, codeBoxY + 22, 230, 52, 8);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#34d399";
  ctx.font = "900 16px 'Inter', sans-serif";
  ctx.fillText("✔ ACTIVATED & USABLE", width - 320, codeBoxY + 54);

  // 7. Footer Meta
  ctx.fillStyle = "#64748b";
  ctx.font = "500 13px 'Inter', sans-serif";
  const dateStr = activatedAt || new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  ctx.fillText(`Generated on ${dateStr} • Single-Use Redeemable at trieducation.in & asiyapp.com`, 80, 615);

  ctx.fillStyle = "#94a3b8";
  ctx.font = "700 13px 'Inter', sans-serif";
  ctx.fillText("VERIFIED BY TRIKONEKT GROWTH ENGINE", width - 380, 615);

  // 8. Load and draw QR code on the right side of value box
  const qrImg = new Image();
  qrImg.crossOrigin = "anonymous";
  qrImg.src = `https://api.qrserver.com/v1/create-qr-code/?size=110x110&data=${encodeURIComponent(
    `TRIKONEKT-ATV:${couponCode}:${amount}`
  )}&bgcolor=ffffff&color=0f172a&margin=1`;

  qrImg.onload = () => {
    try {
      ctx.drawImage(qrImg, width - 210, 80, 110, 110);
    } catch (_) {}
    triggerDownload();
  };

  qrImg.onerror = () => {
    triggerDownload();
  };

  function triggerDownload() {
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Agent_Training_Voucher_${couponCode}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, "image/png");
  }
}
